import React from 'react';
import styled from 'styled-components';
import { Calculate, ContentPasteSearchTwoTone } from '@mui/icons-material';
import { type AcademicQualificationFormProps } from './types';
import { APPLICATION_TEXT } from '../../lang';
import { getOptionsLabel } from '../../getOptionsLabel';
import {
  DocumentBox,
  type FileData,
  IconButton,
} from '../../../../../components';
import {
  GPAConversionDialog,
  GPAConversionDialogV2,
  initialQualificationStatusOptions,
  QUALIFICATION_STATUS_TYPES,
} from '../../../../Application/components/ApplicationForm';
import { PAGE_TEXT } from '../../../../Assessment/lang';

const GPA_TEXT = PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_A;
const ACADEMIC_QUALIFICATION = APPLICATION_TEXT.ACADEMIC_QUALIFICATION;
export const AcademicQualificationForm: React.FunctionComponent<
  AcademicQualificationFormProps
> = (props: AcademicQualificationFormProps) => {
  const { degreeNumber, academicQualification, GSview } = props;
  const [openDialogV1, setOpenDialogV1] = React.useState(false);
  const [openDialogV2, setOpenDialogV2] = React.useState(false);
  const {
    presentIndicator,
    degreeLevel,
    programmeName,
    from,
    to,
    marks,
    convertGPARuleId,
    digitToLetterRule,
    studyMode,
    currentStudyYear,
    institutionName,
    institutionCountry,
    instructionMedium,
    studyField,
    qualificationMeans,
    qualificationStatus,
    awardDate,
    officialMark,
    awardClassification,
    outOfGpa,
    convertedMark,
    expectUpdateDate,
    withdrawDate,
    academicOnlineVerificationCode,
    degreeOnlineVerificationCode,
    officialClassRanking,
    outOfRank,
    certificates,
    transcripts,
    markExplanations,
    classRankProofs,
    otherDocuments,
    gpaIndicator,
    rankIndicator,
  } = academicQualification;
  const hasMark = marks.length > 0;

  const renderData = [
    {
      label: ACADEMIC_QUALIFICATION.NAME_OF_PROGRAMME_AWARD,
      value: programmeName,
      ifShow: true,
      fullWidth: true,
    },
    {
      label: ACADEMIC_QUALIFICATION.INSTITUTION_NAME,
      value: institutionName,
      ifShow: true,
      fullWidth: true,
    },
    {
      label: ACADEMIC_QUALIFICATION.INSTITUTION_COUNTRY,
      value: institutionCountry,
      ifShow: true,
      fullWidth: false,
    },
    {
      label: ACADEMIC_QUALIFICATION.DEGREE_LEVEL,
      value: degreeLevel,
      ifShow: true,
      fullWidth: false,
    },
    {
      label: ACADEMIC_QUALIFICATION.FROM,
      value: from,
      ifShow: true,
      fullWidth: false,
    },
    {
      label: ACADEMIC_QUALIFICATION.TO,
      value: to,
      ifShow: true,
      fullWidth: false,
    },
    {
      label: ACADEMIC_QUALIFICATION.CURRENT_STUDY_YEAR,
      value: currentStudyYear,
      ifShow: presentIndicator,
      fullWidth: false,
    },
    {
      label: ACADEMIC_QUALIFICATION.MODE_OF_STUDY,
      value: studyMode,
      ifShow: true,
      fullWidth: false,
    },
    {
      label: ACADEMIC_QUALIFICATION.MEDIUM_OF_INSTRUCTION,
      value: instructionMedium,
      ifShow: true,
      fullWidth: false,
    },
    {
      label: ACADEMIC_QUALIFICATION.FIELD_OF_STUDY,
      value: studyField,
      ifShow: true,
      fullWidth: GSview,
    },
    {
      label: ACADEMIC_QUALIFICATION.MEDIUM_OF_QUALIFICATION,
      value: qualificationMeans,
      ifShow: true,
      fullWidth: true,
    },
    {
      label: ACADEMIC_QUALIFICATION.QUALIFICATION_STATUS,
      value: getOptionsLabel(
        initialQualificationStatusOptions,
        qualificationStatus,
      ),
      ifShow: true,
      fullWidth: true,
    },
    {
      label: ACADEMIC_QUALIFICATION.DATE_OF_AWARD,
      value: awardDate,
      ifShow: qualificationStatus === QUALIFICATION_STATUS_TYPES.GRADUATED,
      fullWidth: false,
    },
    {
      label: ACADEMIC_QUALIFICATION.EXPECTED_DATE,
      value: expectUpdateDate,
      ifShow:
        qualificationStatus ===
          QUALIFICATION_STATUS_TYPES.WILL_COMPLETE_STUDY_BEFORE ||
        qualificationStatus ===
          QUALIFICATION_STATUS_TYPES.WILL_GRADUATE_IN_AFTER,
      fullWidth: true,
    },
    {
      label: ACADEMIC_QUALIFICATION.WITHDRAWAL_DATE,
      value: withdrawDate,
      ifShow: qualificationStatus === QUALIFICATION_STATUS_TYPES.WITHDRAW,
      fullWidth: GSview,
    },
    {
      label: ACADEMIC_QUALIFICATION.ONLINE_VERIFICATION,
      value: academicOnlineVerificationCode,
      ifShow: true,
      fullWidth: true,
    },
    {
      label: ACADEMIC_QUALIFICATION.DEGREE_VERIFICATION,
      value: degreeOnlineVerificationCode,
      ifShow: true,
      fullWidth: true,
    },
    {
      label: ACADEMIC_QUALIFICATION.OFFICIAL_GPA,
      value: gpaIndicator ? (
        ACADEMIC_QUALIFICATION.NO_OFFICIAL_GPA
      ) : (
        <>
          {officialMark}
          {` / `}
          {outOfGpa}
          <IconButton
            icon={
              <ContentPasteSearchTwoTone
                style={{
                  color: hasMark ? '#951d1d' : 'inherit',
                }}
              />
            }
            disabled={!hasMark}
            onClick={() => {
              setOpenDialogV1(true);
            }}
          />
          {'('}
          <span
            className="text-xs italic"
            dangerouslySetInnerHTML={{
              __html: hasMark
                ? GPA_TEXT.OFFICIAL_GPA.NOTE_2
                : GPA_TEXT.OFFICIAL_GPA.NOTE_1,
            }}
          />
          {')'}
        </>
      ),
      ifShow: true,
      fullWidth: true,
    },
    {
      label: ACADEMIC_QUALIFICATION.CONVERTED_SCORE,
      value: gpaIndicator ? (
        ACADEMIC_QUALIFICATION.NA
      ) : (
        <>
          {convertedMark}
          <IconButton
            icon={
              <Calculate
                style={{
                  color: '#951d1d',
                }}
              />
            }
            onClick={() => {
              setOpenDialogV2(true);
            }}
          />
        </>
      ),
      ifShow: true,
      fullWidth: true,
    },
    {
      label: ACADEMIC_QUALIFICATION.CLASS_RANK,
      value: rankIndicator ? (
        ACADEMIC_QUALIFICATION.NO_CLASS_RANK
      ) : (
        <>
          {officialClassRanking}
          {` / `}
          {outOfRank}
        </>
      ),
      ifShow: true,
      fullWidth: false,
    },
    {
      label: ACADEMIC_QUALIFICATION.AWARD_CLASSIFICATION,
      value: awardClassification,
      ifShow: qualificationStatus === QUALIFICATION_STATUS_TYPES.GRADUATED,
      fullWidth: false,
    },
  ];

  const academicDocs = [
    {
      docTitle: ACADEMIC_QUALIFICATION.FILES_PANEL.CERTIFICATE,
      documents: certificates,
    },
    {
      docTitle: ACADEMIC_QUALIFICATION.FILES_PANEL.TRANSCRIPT,
      documents: transcripts,
    },
    {
      docTitle: ACADEMIC_QUALIFICATION.FILES_PANEL.SYSTEM_EXPLANATION,
      documents: markExplanations,
    },
    {
      docTitle: ACADEMIC_QUALIFICATION.FILES_PANEL.RANKING_PROOF,
      documents: classRankProofs,
    },
    {
      docTitle: ACADEMIC_QUALIFICATION.FILES_PANEL.OTHER_DOCUMENTS,
      documents: otherDocuments,
    },
  ];

  return (
    <Container>
      <h1>{degreeNumber}</h1>

      <div className="grid grid-cols-2 px-2 text-sm w-full my-[1rem] divide-y divide-slate-200">
        {renderData.map((item, index) => {
          return (
            <div
              key={index}
              className={`py-2 ${item.fullWidth ? 'col-span-2' : ''}`}
            >
              <span className="text-[#224295]">{item.label}</span>:&nbsp;&nbsp;
              {item.value}
            </div>
          );
        })}
      </div>
      <hr className="mx-2" />
      <div className="supporting-documents">
        {academicDocs.map((doc, index) => {
          return (
            <div key={index}>
              <h3>{doc.docTitle}</h3>
              <div className="document-display">
                {doc.documents.length > 0 ? (
                  doc.documents.map((file: FileData, index) => {
                    return <DocumentBox key={index} file={file} />;
                  })
                ) : (
                  <div className="text-sm">{APPLICATION_TEXT.NA}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {openDialogV1 && (
        <GPAConversionDialog
          viewMode={true}
          width="680px"
          dialogTitle={ACADEMIC_QUALIFICATION.GPA_DIALOG.DIALOG_V1_TITLE}
          openDialog={openDialogV1}
          onDialogClose={() => {
            setOpenDialogV1(false);
          }}
          onDialogApply={() => {}}
          dialogContent={{
            courses: marks,
            officialMark: officialMark ?? '',
            digitToLetterRule,
          }}
        />
      )}

      {openDialogV2 && (
        <GPAConversionDialogV2
          viewMode={true}
          dialogTitle={ACADEMIC_QUALIFICATION.GPA_DIALOG.DIALOG_V2_TITLE}
          openDialog={openDialogV2}
          onDialogClose={() => {
            setOpenDialogV2(false);
          }}
          // do not pass onDialogApply, to hide apply button
          institutionName={institutionName}
          officialMark={Number(officialMark ?? 0)}
          outOfGpa={Number(outOfGpa ?? 100)}
          convertGPARuleId={convertGPARuleId}
          convertedMark={Number(convertedMark ?? 0)}
          countryRegion={institutionCountry}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  h1 {
    text-align: left !important;
    font-size: 1rem !important;
    font-weight: 500 !important;
  }
  .supporting-documents {
    padding: 0.5rem 0 0 2rem;

    .document-display {
      padding: 0.5rem 1rem;
    }

    h3 {
      font-style: italic;
      color: #8c8c8c;
    }
  }

  .MuiBox-root {
    padding: 0 0 0.1rem 0.5rem;
  }

  .MuiGrid-item {
    padding-left: 0 !important;
  }

  .MuiGrid-container {
    padding: 0.5rem 1rem 0 2rem !important;
  }
`;
